/**
 * index.tsx — Core dispatcher for the CMS Renderer system.
 *
 * Responsibilities:
 *  1. Exports all shared TypeScript interfaces used across cms_renderer:
 *     `SectionData`, `UICompType`, `UiCompTypeOptions`, `PageData`,
 *     `ComponentRenderProps`, `CmsRendererWrapperProps`.
 *  2. Exports `renderFunctions` — a registry mapping component name strings to
 *     React components. Add new CMS components here.
 *  3. Exports `renderSection` — the per-section dispatcher: strips the `render`
 *     prefix from `uiCompType.method`, looks up the component in `renderFunctions`,
 *     and falls back to `Panel` if no match is found.
 *  4. Exports `renderText` — a convenience wrapper for rendering text values
 *     through the `CustText` component.
 *  5. Default export `CmsRenderer` — the top-level client component. Parses the
 *     serialized `message` prop (JSON string from the RSC layer), iterates the
 *     `cmsData.order` array, and calls `renderSection` for each section key.
 *
 * Called by:
 *  - `[vendor]/static/[...slug]/page.tsx` (RSC) serialises PageData to JSON string
 *    and passes it to `<CmsRenderer message={...} />`.
 *  - Individual components (Accordion, Panel, etc.) call `renderSection` to
 *    recursively render nested sections.
 */
'use client'

import React, { useEffect, useRef, useState } from "react";
import { useLayout } from "@/app/providers/LayoutProvider";
import cssStyles from "./cms_renderer.module.scss";

//TODO : need to refactor the existing product_cards.tsx to replace below shop_product_cards.tsx
import './global_functions';

// Import all components from their respective files as default
import Accordion from './Accordion';
import CategoriesCarousel from './CategoriesCarousel';
import CirclerImageButton from './CirclerImageButton';
import ContentOnImage from './ContentOnImage';
import CustButton from './CustButton';
import CustCarousel from './CustCarousel';
import CustImage from './CustImage';
import CustText from './CustText';
import CustVideo from './CustVideo';
import ImageButton from './ImageButton';
import ItemsGrid from './ItemsGrid';
import MarkdownTextPanel from './MarkdownTextPanel';
import OnPageLoadAction from './OnPageLoadAction';
import OpenPopup from './OpenPopup';
import Panel from './Panel';
import ProductsCarousel from './ProductsCarousel';
import Slider from './Slider';
import Title from './Title';
import SubTitle from './SubTitle';
import ExpandableContent from './ExpandableContent';
import CollagePanel from './CollagePanel';
import Parallax from './Parallax';


/**
 * Represents a single CMS section or nested content block.
 *
 * Every JSON section object in a CMS page maps to this interface. The active
 * component is selected via `uiCompType.method`; if omitted the section renders
 * as a `Panel`.
 *
 * Common field groups:
 *  - **Content:** `title`, `subTitle`, `description`, `text`, `para`, `content`
 *  - **Media:** `imageUrl`, `imageUrls`, `videoUrl`
 *  - **Structure:** `panels`, `items`, `gridItems`, `buttons`, `rows`
 *  - **Behaviour:** `link`, `newTab`, `onClick`, `include`, `preprocess`
 *  - **Rendering:** `uiCompType` (component + styles)
 *  - **Analytics:** `trackId`, `trackInfo`
 *  - **Index signature:** `[key: string]: any` — allows arbitrary Panel keys such
 *    as `title-center-bold` or `imageUrl-h400px`.
 */
export interface SectionData {
    backgroundImageUrl?: string;
    buttons?: SectionData[];
    color?: string;
    content?: string | string[] | SectionData; // Used by ContentOnImage
    description?: string | string[];
    footer?: SectionData;
    gridItems?: SectionData[];
    header?: SectionData;
    id?: string;
    imagePanel?: SectionData;
    imageUrl?: string;
    imageUrls?: string[];
    items?: SectionData[] | string[];
    link?: string;
    name?: string;
    newTab?: boolean;
    order?: string[];
    panels?: SectionData[];
    para?: string;
    subTitle?: string;
    text?: string | string[];
    title?: string;
    space?: string;
    trackId?: string;
    trackInfo?: {
        event?: string;
        section?: string;
        [key: string]: any; // Allow other properties in trackInfo
    };
    preProcess?: { // Corrected casing from preProcess to preprocess
        method: string;
        input: any;
    };
    preprocess?: { // Added for consistency with README
        method: string;
        input: any;
    };
    type?: string;
    uiCompType?: UICompType;
    onClick?: { // For CustButton
        method: string;
        input: any;
    };
    include?: string; // For including shared sections

    // For OpenPopup
    dialogProps?: any;
    contentMethod?: string;
    component?: string; // Path for dynamic component import
    input?: any; // Props for dynamically imported component

    // For OnPageLoadAction
    postLoadAction?: {
        method: string;
    };
    inputObj?: any;
    videoUrl?: string; // For CustVideo

    [key: string]: any; // <-- Add this index signature to allow string keys
}

/**
 * Configuration options passed as `uiCompType.options` to individual components.
 *
 * Options are component-specific — only the fields relevant to the rendered
 * component need to be supplied. Unknown fields are silently ignored.
 *
 * Key option groups:
 *  - **Layout:**  `align`, `columns`, `display`, `grid`, `spacing`, `xs/md/lg`
 *  - **Sizing:**  `height`, `width`, `imageWidth`, `imageHeight`, `textWidth`
 *  - **Carousel:** `carouselProps` (ReactMultiCarousel props forwarded to `Slider`)
 *  - **Video:**  `autoPlay`, `loop`, `muted`, `controls`, `poster`, `preloadVideo`,
 *               `thumbnail`, `playOnMouseEnter`
 *  - **Image:**  `objectFit`, `quality`
 *  - **Text:**   `variant`, `markdown`, `gutterBottom`, `noWrap`
 *  - **Grid:**   `swapItems`, `swapRowItems`, `swapColumnItems`
 *  - **Popup:**  `component`, `sx`
 */
export interface UiCompTypeOptions {
    borderRadius?: string;
    playOnFocusOnly?: any;
    playOnMouseEnter?: any;
    thumbnail?: any;
    align?: string;
    color?: string; // For CustButton
    columns?: number;
    component?: string; // For CustText, OpenPopup
    display?: string;
    gutterBottom?: string;
    height?: string; // For CategoriesCarousel, CirclerImageButton, ImageButton, ProductsCarousel
    imageWidth?: string;
    imageHeight?: string;
    textWidth?: string;
    textHeight?: string;
    swapItems?: boolean; // For ItemsGrid
    swapRowItems?: boolean; // For ItemsGrid
    swapColumnItems?: boolean; // For ItemsGrid
    minHeight?: number; // For MarkdownTextPanel
    noWrap?: boolean;
    parasCount?: number; // For MarkdownTextPanel
    shape?: string;
    size?: string; // For CustButton
    splitRaito?: number;
    sx?: string;
    width?: string; // For CategoriesCarousel, CirclerImageButton, ImageButton, ItemsGrid item
    variant?: string; // For CustText
    markdown?: boolean; // For CirclerImageButton, CustText
    grid?: string; // For ItemsGrid
    spacing?: number | string; // For ItemsGrid
    objectFit?: string; // For CustImage
    carouselProps?: any; // For Slider
    xs?: number | 'auto'; // For ItemsGrid item
    md?: number | 'auto'; // For ItemsGrid item
    lg?: number | 'auto'; // For ItemsGrid item
    preload?: boolean; // For ContentOnImage background image
    newTab?: boolean; // For CustButton, ImageButton
    // For CustVideo
    autoPlay?: boolean;
    loop?: boolean;
    muted?: boolean;
    poster?: string;
    preloadVideo?: "auto" | "metadata" | "none"; // Renamed from 'preload' to avoid conflict
    playsInline?: boolean;
    controls?: boolean;
    quality?: number; // For CustImage (moved from styles as per README example)
    overlayColor?: string; // For Parallax — CSS color/gradient for the overlay layer
    backgroundPosition?: string; // For Parallax — CSS background-position value
    // For CollagePanel
    gap?: number | string; // Gap between items
    rowHeight?: number; // Row height in pixels for standard/quilted/woven
    mRowHeight?: number; // Override rowHeight on mobile
    mColumns?: number; // Override columns on mobile
    cols?: number; // Per-item column span (quilted variant)
    rows?: number; // Per-item row span (quilted variant)
    colSpan?: number; // Alias for cols (per-item column span)
    rowSpan?: number; // Alias for rows (per-item row span)
    mCols?: number; // Per-item column span on mobile
    mRows?: number; // Per-item row span on mobile
    // For ProductsCarousel
    hidePricing?: boolean; // Hide price/shipping/view-in-room on product cards
    showLoginOverlay?: boolean; // Show "Login to view pricing" overlay on product cards
}

/**
 * Describes how a section should be rendered and styled.
 *
 * `method` selects the component: strip the `render` prefix and look up in
 * `renderFunctions`, e.g. `"renderSlider"` → `Slider`.
 *
 * **Style slot hierarchy (outermost → innermost):**
 * ```
 * sectionStyles       — <section> tag wrapper (applied by renderSection)
 * parentStyles        — outer container div
 * containerStyles     — inner container / content area
 * styles              — primary element (image, card, text block)
 * imageContainerStyles— wrapper around <Image> / <img>
 * imageStyles         — the <Image> / <img> element itself
 * itemStyles          — each child item in a list/grid
 * ```
 * Each slot has a mobile (`m*`) counterpart that overrides on small screens.
 *
 * `className` is resolved via the `cms_renderer.module.scss` CSS module.
 */
export interface UICompType {
    method?: string;
    options?: UiCompTypeOptions;
    className?: string;

    styles?: React.CSSProperties;
    mStyles?: React.CSSProperties;
    sectionStyles?: React.CSSProperties;
    mParentStyles?: React.CSSProperties;
    parentStyles?: React.CSSProperties;
    mContainerStyles?: React.CSSProperties;
    containerStyles?: React.CSSProperties;
    imageContainerStyles?: React.CSSProperties;
    imageStyles?: React.CSSProperties;
    itemStyles?: React.CSSProperties;
    mItemStyles?: React.CSSProperties;
    itemContainerStyles?: React.CSSProperties;  // Wrapper div around each slide item in Slider
    mItemContainerStyles?: React.CSSProperties; // Mobile overrides for itemContainerStyles
    leftArrowStyles?: React.CSSProperties; // For Slider
    rightArrowStyles?: React.CSSProperties; // For Slider
}

/**
 * Root structure of a CMS page JSON file.
 *
 * Fetched from `{CMS_HOST_URL}{env}/{slug}.json` by `getCmsData`.
 *
 * @property title    - Page `<title>` tag value.
 * @property order    - Ordered array of section keys; controls render sequence.
 * @property sections - Map of section key → `SectionData` objects.
 * @property env      - Overrides the default environment for nested fetches.
 * @property trackInfo- Analytics metadata emitted on page view.
 */
export interface PageData {
    page?: string;
    title: string;
    env?: string;
    order: string[];
    description?: string;
    headerJson?: string;
    headerConfig?: Record<string, any>;
    sections: {
        [key: string]: SectionData;
    };
    trackInfo?: {
        pageType?: string;
        pageName?: string;
        [key: string]: any; // Allow other properties in trackInfo
    };
}

// Define ComponentRenderProps
export interface ComponentRenderProps {
    vendorName?: string;
    cmsData?: PageData; // The full parsed CMS data
    userInfo?: any; // Adjust as per your userInfo structure
    favorites?: any[]; // Adjust as per your favorites structure
    setFavorites?: (favorites: any[]) => void; // Adjust as per your setFavorites structure
    // Add any other shared/contextual props needed by components
}

// Define CmsRendererWrapperProps
export interface CmsRendererWrapperProps {
    message: string | { vendorName: string; cmsData: PageData }; // Can be JSON string or parsed object
    sectionCmsData?: SectionData;
    sectionCmsKey?: string;
    userInfo?: any;
    favorites?: any[];
    setFavorites?: (favorites: any[]) => void;
}


const CMS_HOST = process.env.CMS_HOST;
let CMS_ID_COUNTER = 0;

/**
 * Convenience helper that renders a text value through the `CustText` component.
 *
 * Constructs a synthetic `SectionData` with a `text` field and merges the
 * provided `uiCompType` styles, then delegates to the `CustText` renderer.
 *
 * @param text            - String or string array to render.
 * @param className       - CSS module class name.
 * @param uiCompType      - Style and option overrides.
 * @param props           - Shared `ComponentRenderProps`.
 * @param variant         - MUI Typography variant (e.g. `'h1'`, `'body2'`).
 * @param extractedStyles - Extra inline styles merged on top of `uiCompType.styles`.
 * @returns React node or `null` if text is falsy.
 */
export const renderText = (text: string | string[], className: string,
    uiCompType: any, props: ComponentRenderProps, variant?: string, extractedStyles?: React.CSSProperties) => {
    if (!text) return null;
    const CustTextComponent = renderFunctions.CustText;
    const sectionData = {
        text,
        uiCompType: {
            className,
            ...uiCompType,
            options: {
                variant,
                ...uiCompType?.options,
            },
            styles: {
                ...uiCompType?.styles,
                ...extractedStyles,
            }
        }
    };
    return <CustTextComponent sectionData={sectionData} props={props} />;
};

export type RenderFunctionType = {
    [key: string]: React.ComponentType<any> | ((...args: any[]) => any);
};

import { useDeviceType } from "@/hooks/useDeviceType";

export const isIosDevice = (): boolean => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};


/**
 * Registry mapping CMS component names to their React implementations.
 *
 * `renderSection` looks up components here after stripping the `render` prefix
 * from `uiCompType.method` (e.g. `"renderSlider"` → `"Slider"` → `Slider`).
 *
 * **To register a new component:**
 * 1. Import the component at the top of this file.
 * 2. Add it to this map.
 * 3. In the CMS JSON, set `uiCompType.method: "renderYourComponent"`.
 *
 * The fallback when a key is not found is the `Panel` component.
 */
export const renderFunctions: RenderFunctionType = {
    Accordion,
    CategoriesCarousel,
    CirclerImageButton,
    CollagePanel,
    ContentOnImage,
    Parallax,
    CustButton,
    CustCarousel,
    CustImage,
    CustText,
    CustVideo,
    ImageButton,
    ItemsGrid,
    MarkdownTextPanel,
    OnPageLoadAction,
    OpenPopup,
    Panel,
    ProductsCarousel,
    Slider
};

/**
 * Dispatches a single CMS section to the correct React component.
 *
 * Resolution order:
 *  1. Read `sectionData.uiCompType.method`.
 *  2. Strip the `render` prefix → lookup key in `renderFunctions`.
 *  3. Special-case `renderOpenPopup` → `OpenPopup` and
 *     `renderOnPageLoadAction` → `OnPageLoadAction`.
 *  4. Fall back to `Panel` if no match.
 *
 * When `isSection` is `true` the component is wrapped in a `<section>` tag with
 * `sectionStyles` applied (used for top-level page sections; nested sections pass
 * `false` to avoid extra DOM nesting).
 *
 * @param sectionKey            - CMS section key (used as React `key` prop).
 * @param sectionData           - Parsed section data from the CMS JSON.
 * @param componentRenderProps  - Shared runtime props (vendorName, userInfo, …).
 * @param isSection             - Wrap in `<section>` tag when `true`.
 * @returns A React element or `null` if `sectionData` is falsy.
 */
export const renderSection = (sectionKey: string, sectionData: SectionData, componentRenderProps: ComponentRenderProps, isSection?: boolean) => {
    //console.log("renderSection...sectionKey: ", sectionKey);
    // sectionData is now directly passed, no need to fetch from componentRenderProps.cmsData.sections
    // sectionData = sectionData || componentRenderProps.cmsData?.sections[sectionKey]; 
    if (!sectionData) {
        console.warn(`No section data found for key: ${sectionKey}. Skipping render.`); // Changed to warn
        return null;
    }
    // Handle cases where uiCompType might be missing after include or by mistake
    const uiCompTypeMethod = sectionData.uiCompType?.method;
    let renderFunction: string = uiCompTypeMethod?.replace(/^render/, '') || "Panel";

    // If method is OpenPopup, ensure it's handled directly by its component, not as a generic panel content.
    // The OpenPopup component itself will decide how to render its internal content.
    if (uiCompTypeMethod === 'renderOpenPopup') {
        renderFunction = 'OpenPopup';
    } else if (uiCompTypeMethod === 'renderOnPageLoadAction') {
        renderFunction = 'OnPageLoadAction';
    }

    const ComponentToRender = renderFunctions[renderFunction] || Panel;


    return (
        isSection ? (
            <section key={sectionKey} style={sectionData.uiCompType?.sectionStyles} className={cssStyles.section}>
                <ComponentToRender sectionData={sectionData} props={componentRenderProps} />
            </section>
        ) : (
            <>
                <ComponentToRender sectionData={sectionData} props={componentRenderProps} />
            </>
        )
    );
};

/**
 * CmsRenderer component is responsible for rendering CMS sections based on the provided props.
 * 
 * @param {any} props - The properties passed to the component.
 * @param {string} props.message - A JSON string containing vendorName and cmsData.
 * @param {object} props.sectionCmsData - Data for a specific CMS section.
 * @param {string} props.sectionCmsKey - Key for a specific CMS section.
 * 
 * @returns {JSX.Element} The rendered CMS sections.
 * 
 * The component first parses the `props.message` to extract `vendorName` and `cmsData`.
 * It then checks if `sectionCmsData` and `sectionCmsKey` are provided.
 * If they are, it renders the specific section using `renderSection` function.
 * If not, it iterates over the `order` array in `cmsData` to render each section in the specified order.
 * 
 * The `order` array in the `cmsData` is crucial as it determines the sequence in which the sections are rendered.
 * Each key in the `order` array corresponds to a section in the `sections` object of `cmsData`.
 * The `renderSection` function is called for each section key, passing the section data and other props.
 */

/**
 * Merges page-level headerConfig into the existing headerMenuJson in layout context.
 * Runs once on mount — headerConfig is static SSR data that doesn't change.
 */
function HeaderConfigApplier({ headerConfig }: { headerConfig: Record<string, any> }) {
    const { headerMenuJson, setHeaderMenuJson } = useLayout();
    const applied = useRef(false);

    useEffect(() => {
        if (!applied.current && setHeaderMenuJson) {
            const current = headerMenuJson || {};
            setHeaderMenuJson({
                ...current,
                config: { ...current.config, ...headerConfig }
            });
            applied.current = true;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
}

const CmsRenderer = (props: CmsRendererWrapperProps) => {
    let vendorName: string;
    let cmsData: PageData; // Use PageData type

    try {
        if (typeof props.message === 'string') {
            const parsedMessage = JSON.parse(props.message);
            vendorName = parsedMessage.vendorName;
            cmsData = parsedMessage.cmsData;
        } else if (typeof props.message === 'object' && props.message !== null) {
            vendorName = props.message.vendorName;
            cmsData = props.message.cmsData;
        } else {
            throw new Error("props.message is undefined or not a valid type");
        }
    } catch (error) {
        console.error("Error parsing CMS data from props.message:", error, "props.message was:", props.message);
        return <div>Error loading CMS data. Please try again later. Error : {String(error)}</div>;
    }

    const { sectionCmsData, sectionCmsKey, userInfo, favorites, setFavorites } = props;

    if (!cmsData || (!cmsData.order && !sectionCmsData)) {
        console.warn("CMS data is missing 'order' array or sectionCmsData is not provided.");
        // return <div>CMS content structure is invalid.</div>; // Or render nothing
    }

    const componentRenderProps: ComponentRenderProps = {
        vendorName,
        cmsData,
        userInfo,
        favorites,
        setFavorites
    };


    return (
        <>
            {/* If the page JSON has headerConfig, merge it into the layout context headerMenuJson */}
            {cmsData?.headerConfig && <HeaderConfigApplier headerConfig={cmsData.headerConfig} />}
            {(sectionCmsData && sectionCmsKey)
                ? <React.Fragment key={sectionCmsKey}>
                    {renderSection(sectionCmsKey, sectionCmsData, componentRenderProps, true)}
                </React.Fragment>
                : cmsData?.order?.map((secKey: string, index: number) => ( // Added optional chaining for cmsData.order
                    <React.Fragment key={secKey}>
                        {secKey && cmsData.sections && cmsData.sections[secKey] && renderSection(secKey, cmsData.sections[secKey], componentRenderProps, (index == 0 ? false : true))}
                    </React.Fragment>
                ))}
        </>
    )
}

export default CmsRenderer;